import { Typography, type TypographyProps } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { type FragmentOf, graphql, readFragment, type ResultOf } from "@/graphql"

const InTheNameOfUsers_AccountUserFragment = graphql(`
    fragment InTheNamesOfUsers_AccountUser on AccountUserType {
        familyName
        givenName
    }
`)

type AccountUser = ResultOf<typeof InTheNameOfUsers_AccountUserFragment>

function formatAccountUserName(user: AccountUser) {
    return `${user.givenName} ${user.familyName.toUpperCase()}`
}

type InTheNamesOfUsersProps = Omit<TypographyProps, "children"> & {
    users: Array<FragmentOf<typeof InTheNameOfUsers_AccountUserFragment>>
}

export function InTheNamesOfUsers({
    users: userFragments,
    component = "p",
    textStyle = "h4",
    ...typographyProps
}: InTheNamesOfUsersProps) {
    const users = readFragment(InTheNameOfUsers_AccountUserFragment, userFragments)

    const t = useTranslations("InTheNamesOfUsers")

    const [lastUser, ...otherUsers] = users
        .filter((user) => !!user.familyName && !!user.givenName)
        .reverse()

    if (!lastUser) {
        return null
    }

    if (!otherUsers.length) {
        return (
            <Typography
                component={component}
                textStyle={textStyle}
                {...typographyProps}
            >
                {t("inTheNameOf", { name: formatAccountUserName(lastUser) })}
            </Typography>
        )
    }

    const formattedUsersNames = otherUsers
        .map(formatAccountUserName)
        .join(", ")
        .concat(` ${t("separator")} ${formatAccountUserName(lastUser)}`)

    return (
        <Typography
            component={component}
            textStyle={textStyle}
            {...typographyProps}
        >
            {t("inTheNamesOf", { names: formattedUsersNames })}
        </Typography>
    )
}

InTheNamesOfUsers.messages = ["InTheNamesOfUsers"] as const

InTheNamesOfUsers.fragments = {
    accountUser: InTheNameOfUsers_AccountUserFragment,
}
